import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMsal } from '@azure/msal-react';
import { itsRequest } from 'http/authConfig';
import PublicShell from './PublicShell';
import { useTranslation } from 'react-i18next';
import useWindowsDimension from 'functions/useWindowsDimension';

type Plan = {
  id: string;
  nameKey: string;
  price: number;
  automation: number | string;
  appPrice: number;
  descKey: string;
  featureKey: string;
};

const plans: Plan[] = [
  {
    id: 'starter',
    nameKey: 'landing.pricing.plans.starter.name',
    descKey: 'landing.pricing.plans.starter.desc',
    price: 499,
    automation: 'ikke inkludert',
    appPrice: 15,
    featureKey: 'landing.pricing.plans.starter.features',
  },
  {
    id: 'scale',
    nameKey: 'landing.pricing.plans.scale.name',
    descKey: 'landing.pricing.plans.scale.desc',
    price: 499,
    automation: 1499,
    appPrice: 11,
    featureKey: 'landing.pricing.plans.scale.features',
  },
];

type CartState = Record<string, number>;

const PricingPage: React.FC = () => {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const { height } = useWindowsDimension();

  const [cart, setCart] = useState<CartState>({});
  const [form, setForm] = useState({
    company: '',
    name: '',
    email: '',
  });

  const total = useMemo(
    () =>
      Object.entries(cart).reduce((acc, [planId, users]) => {
        const plan = plans.find((p) => p.id === planId);
        if (!plan) return acc;
        const usersNum = Math.max(users, 1);
        const userCost = usersNum * plan.price;
        return (
          acc +
          userCost +
          (typeof plan.automation === 'number' ? plan.automation : 0)
        );
      }, 0),
    [cart]
  );

  const handleToggle = (planId: string) => {
    setCart(() => {
      // Kart kan bare ha én plan; toggle betyr enten tom eller kun denne
      return { [planId]: 1 };
    });
  };

  const handleUsersChange = (planId: string, value: number) => {
    setCart((prev) => ({
      ...prev,
      [planId]: Math.max(1, Math.floor(value) || 1),
    }));
  };

  const handleChange =
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = () => {
    instance.loginRedirect(itsRequest);
  };

  const chartHeight = height >= 1200 ? 468 : height >= 900 ? 333 : 24;
  const chartFontsize = height >= 1200 ? 64 : height >= 900 ? 48 : 24;

  const chipFontsize = height >= 1200 ? 32 : height >= 900 ? 24 : 16;
  const leftSideFontsize = height >= 1200 ? 22 : height >= 900 ? 14 : 14;

  return (
    <PublicShell>
      <Grid
        container
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          padding: 0,
          height: height - 188,
          p: 2,
        }}
      >
        {/* LEFT SIDE : Plans section */}
        <Grid
          item
          xs={6}
          sx={{
            width: '100%',
            minHeight: height - 248,
            maxHeight: height - 248,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              minHeight: 40,
              maxHeight: 40,
              mb: 2,
            }}
          >
            <Chip
              label={t(
                'landing.pricing.title',
                'choose the plan that fits your needs best'
              )}
              color='secondary'
              sx={{ width: 'fit-content', fontSize: chipFontsize }}
            />
          </Grid>
          <Grid container spacing={2}>
            {plans.map((plan) => {
              const selectedUsers = cart[plan.id] ?? 0;
              const inCart = selectedUsers > 0;
              const features = t(plan.featureKey, {
                returnObjects: true,
              }) as string[];
              const perUser = t('landing.pricing.table.headerQuantity', {
                count: plan.price,
                defaultValue: `kost: ${plan.price}`,
              });

              const autoCost = (typeof plan.automation) === 'number'  ? 
              t('landing.pricing.table.rowAutomation', {
                automation: plan.automation,
                defaultValue: `${plan.automation} kr`,
              }) : t('landing.pricing.table.notIncluded', 'ikke inkludert');

              const appCost = plan.appPrice > 0 ? t('landing.pricing.table.rowApp', {
                price: plan.appPrice,
                defaultValue: `${plan.appPrice} kr`,
              }) : '';

              return (
                <Grid item xs={12} md={6} key={plan.id} sx={{ p: 2 }}>
                  <Paper
                    variant='elevation3'
                    sx={{
                      height: '100%',
                      border: inCart
                        ? '1px solid rgba(255, 255, 255, 0.63)'
                        : '1px solid rgba(255,255,255,0.08)',
                      backgroundColor: inCart
                        ? 'rgba(2, 131, 90, 0.5)'
                        : 'background.paper',
                    }}
                  >
                    <Box sx={{ p: 3, height: '100%' }}>
                      <Stack spacing={1.5}>
                        <Typography
                          sx={{
                            fontSize: leftSideFontsize * 1.5,
                            fontWeight: 600,
                            letterSpacing: 1.5,
                          }}
                        >
                          {t(plan.nameKey).toUpperCase()}
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: leftSideFontsize * 2,
                              fontWeight: 800,
                            }}
                          >
                            {perUser}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: leftSideFontsize * 1.2,
                              fontWeight: 400,
                            }}
                          >
                            {t(`landing.pricing.table.automation`, {
                              automation: autoCost,
                              defaultValue: `Automatisering: ${autoCost}`,
                            })}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: leftSideFontsize * 1.2,
                              fontWeight: 800,
                            }}
                          >
                            App: {appCost}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: leftSideFontsize,
                            color: 'text.secondary',
                          }}
                        >
                          {t(plan.descKey)}
                        </Typography>
                        <Stack
                          spacing={0.5}
                          sx={{
                            mt: 1,
                            height: leftSideFontsize * 7 * 2,
                            //backgroundColor: 'rgba(255, 255, 255, 0.4)',
                          }}
                        >
                          {features.map((feature) => (
                            <Stack
                              key={feature}
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              <CheckCircleIcon
                                color='secondary'
                                fontSize='small'
                              />
                              <Typography
                                sx={{
                                  fontSize: leftSideFontsize,
                                  fontWeight: 800,
                                  letterSpacing: 1,
                                }}
                              >
                                {feature}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <Button
                          variant={inCart ? 'outlined' : 'contained'}
                          startIcon={<AddShoppingCartIcon />}
                          color='secondary'
                          onClick={() => handleToggle(plan.id)}
                        >
                          {inCart
                            ? t('landing.pricing.removeFromCart')
                            : t('landing.pricing.addToCart')}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* RIGHT SIDE : Cart and Registration section */}
        <Grid
          item
          xs={6}
          sx={{
            width: '100%',
            minHeight: height - 248,
            maxHeight: height - 248,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              width: '100%',
              minHeight: 40,
              maxHeight: 40,
              mb: 2,
            }}
          >
            <Chip
              label={t('landing.pricing.chart', 'Handlekurv og registrering')}
              color='secondary'
              sx={{ width: 'fit-content', fontSize: chipFontsize }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              mb: 2,
              backgroundColor: 'red',
            }}
          >
            <Paper
              variant='elevation3'
              sx={{
                p: 2,
                height: chartHeight,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
              }}
            >
              <Stack spacing={2}>
                <Typography
                          sx={{
                            fontSize: leftSideFontsize * 1.5,
                            fontWeight: 600,
                            letterSpacing: 2,
                          }}
                        >
                  {t('landing.pricing.cartTitle').toUpperCase()}
                </Typography>
                {Object.keys(cart).length === 0 ? (
                  <Typography
                          sx={{
                            fontSize: leftSideFontsize * 1.5,
                            fontWeight: 500,
                            letterSpacing: 3,
                          }}
                        >
                    {t('landing.pricing.cartEmpty')}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {Object.entries(cart).map(([planId, users]) => {
                      const plan = plans.find((p) => p.id === planId);
                      if (!plan) return null;
                      const usersNum = Math.max(users, 1);
                      const userCost = usersNum * plan.price;
                      const planTotal =
                        userCost +
                        (typeof plan.automation === 'number'
                          ? plan.automation
                          : 0);
                      return (
                        <Box
                          key={plan.id}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.08)',
                            backgroundColor: 'background.paper',
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr 1fr',
                            gap: 0,
                            alignItems: 'center',
                          }}
                        >                         
                        
                          <Typography                          
                            sx={{ 
                              gridColumn: '1 / span 4', 
                              fontWeight: 600, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {t(plan.nameKey).toUpperCase()}
                          </Typography>
                          <Typography
                            sx={{ 
                              gridColumn: '1 / span 4',
                              fontWeight: 400, 
                              fontSize: leftSideFontsize * 1,
                              letterSpacing: 1.5,
                             }}
                          >
                          {t('landing.pricing.table.adjust', 
                            'Juster antall brukere og se linjekostnader.')}
                          </Typography>                         

                          {/* Header row */}
                          <Typography                          
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: leftSideFontsize * 1.2,
                              letterSpacing: 1.5,
                            }}
                          >
                            {t('landing.pricing.table.headerLicense', 'Brukerlisens')}
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: leftSideFontsize * 1.2,
                              letterSpacing: 1.5,
                            }}
                          >
                            {t('landing.pricing.table.headerQty', 'Antall')}
                            </Typography>
                          <Typography                          
                            sx={{ 
                             fontWeight: 600, 
                              fontSize: leftSideFontsize * 1.2,
                              letterSpacing: 1.5,
                            }}
                          >
                            {t('landing.pricing.table.headerUnitPrice', 'Stk pris')}
                            </Typography>
                          <Typography                          
                            sx={{ 
                             fontWeight: 600, 
                              fontSize: leftSideFontsize * 1.2,
                              letterSpacing: 1.5,
                            }}
                          >
                            {t('landing.pricing.table.headerLinePrice', 'Linjepris')}
                            </Typography>

                          {/* Row A: brukerlisens */}
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {t('landing.pricing.table.rowLicense', 'Brukerlisens')}
                            </Typography>
                          <TextField
                            type='number'
                            size='small'
                            inputProps={{ min: 1 }}
                            value={usersNum}
                            onChange={(e) =>
                              handleUsersChange(plan.id, Number(e.target.value))
                            }
                            sx={{ width: '100%' }}
                          />
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {plan.price} {t('landing.pricing.cartItemPrice')}
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {userCost} {t('landing.pricing.cartItemPrice')}
                          </Typography>

                          {/* Row B: automatisering (MS lisenshåndtering) */}
                         <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            Automatisering (MS lisenshåndtering)
                          </Typography>
                         <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            -
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {plan.automation}{' '}
                            {t('landing.pricing.cartItemPrice')}
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {plan.automation}{' '}
                            {t('landing.pricing.cartItemPrice')}
                          </Typography>

                          {/* Row C: Automatisk faktureringsoversikt */}
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            Automatisk faktureringsoversikt
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            -
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            Inkludert
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            0
                          </Typography>

                          {/* Row D: App-seter (tillegg) */}
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            App-seter (tillegg)
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {usersNum}
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {plan.appPrice} {t('landing.pricing.cartItemPrice')}
                          </Typography>
                          <Typography                          
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: leftSideFontsize * 1.5,
                              letterSpacing: 1.5,
                            }}
                          >
                            {usersNum * plan.appPrice}{' '}
                            {t('landing.pricing.cartItemPrice')}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Divider />
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: leftSideFontsize * 1.5, letterSpacing: 1.5 }}>
                        {t('landing.pricing.sum')}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: leftSideFontsize * 1.5, letterSpacing: 1.5 }}>
                        {total} {t('landing.pricing.cartItemPrice')}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              variant='elevation3'
              sx={{
                p: 3,
                minHeight: 420,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Stack spacing={2}>
                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  {t('landing.pricing.registrationTitle')}
                </Typography>
                <TextField
                  label={t('landing.pricing.companyLabel')}
                  value={form.company}
                  onChange={handleChange('company')}
                />
                <TextField
                  label={t('landing.pricing.nameLabel')}
                  value={form.name}
                  onChange={handleChange('name')}
                />
                <TextField
                  label={t('landing.pricing.emailLabel')}
                  type='email'
                  value={form.email}
                  onChange={handleChange('email')}
                />
                <Button
                  variant='contained'
                  color='secondary'
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleSubmit}
                  disabled={
                    Object.keys(cart).length === 0 ||
                    !form.email ||
                    !form.name ||
                    !form.company
                  }
                >
                  {t('landing.pricing.submit')}
                </Button>
                <Typography variant='caption' color='text.secondary'>
                  {t('landing.pricing.submitHint')}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </PublicShell>
  );
};

export default PricingPage;
